import styled from "styled-components";

import { tokens } from "../../../theme/tokens";
import { fontSizes, lineHeights } from "../../../shared/styles/typography";

const BP = tokens.layout.mobileFirstExpandPx;
const info = "#5f33e1";
const infoDark = "#4a2bb8";

function letterTileBorder(p: { $active: boolean; $soft: boolean }): string {
  if (p.$active) return infoDark;
  if (p.$soft) return info;
  return "var(--color-line-in-white)";
}

function letterTileBg(p: { $active: boolean; $soft: boolean }): string {
  if (p.$active) return info;
  if (p.$soft) return "rgba(95, 51, 225, 0.12)";
  return "transparent";
}

function letterTileLibColor(p: { $active: boolean; $soft: boolean }): string {
  if (p.$active) return "#fff";
  if (p.$soft) return info;
  return "var(--color-text-complement)";
}

export const PageScroll = styled.div`
  padding: 0 1.2rem 6rem;
  max-width: var(--layout-app-shell-max);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  @media (min-width: ${BP}px) {
    padding-left: max(1.2rem, env(safe-area-inset-left));
    padding-right: max(1.2rem, env(safe-area-inset-right));
  }
`;

export const WordHeaderCard = styled.div`
  border-radius: 20px;
  background: ${info};
  border: 3px solid ${infoDark};
  padding: 1.2rem 1.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const WordHeaderTitle = styled.h2`
  margin: 0;
  font-size: ${fontSizes.xl}px;
  line-height: ${lineHeights.xl}px;
  font-weight: var(--font-weight-bold);
  color: #fff;
  flex: 1;
`;

export const WordHeaderThumb = styled.button`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.15);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const WordHeaderLetterFallback = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-family: "Libras2020", sans-serif;
    font-size: 36px;
    color: #fff;
    line-height: 1;
  }
`;

export const CaptureBox = styled.div`
  border-radius: 20px;
  background: #fff;
  border: 2px solid #e2e8f0;
  overflow: hidden;
`;

export const CaptureInner = styled.div`
  padding: 1.6rem 1.2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

export const HeroLetterWrap = styled.div`
  position: relative;
  border-radius: 16px;
  border: 3px solid ${info};
  background: #f8fafc;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.4rem 1rem;
`;

export const FullscreenCornerBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  padding: 10px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(95, 51, 225, 0.9);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BigLibras = styled.span<{ $hasFont: boolean }>`
  display: block;
  font-family: ${(p) => (p.$hasFont ? '"Libras2020", sans-serif' : "inherit")};
  font-size: clamp(96px, 28vw, 120px);
  color: ${info};
  line-height: 1;
  text-align: center;
`;

export const BigLatin = styled.span`
  display: block;
  margin-top: 0.8rem;
  font-size: ${fontSizes["2xl"]}px;
  line-height: ${lineHeights["2xl"]}px;
  font-weight: var(--font-weight-bold);
  color: #1e293b;
  text-align: center;
`;

export const PerWordSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding-top: 0.5rem;
`;

export const PerWordCard = styled.div`
  border-radius: 16px;
  border: 2px solid ${info};
  background: #fff;
  padding: 1.2rem;
`;

export const PerWordLabel = styled.div`
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: var(--font-weight-semibold);
  color: #64748b;
  margin-bottom: 0.8rem;
`;

export const LettersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

export const LetterTile = styled.div<{ $active: boolean; $soft: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 72px;
  min-height: 96px;
  padding: 10px;
  border-radius: 12px;
  border: 2px solid ${(p) => letterTileBorder(p)};
  background: ${(p) => letterTileBg(p)};

  .lib {
    font-family: "Libras2020", sans-serif;
    font-size: 48px;
    line-height: 1;
    color: ${(p) => letterTileLibColor(p)};
  }

  .lat {
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
    font-weight: var(--font-weight-semibold);
    color: ${(p) => letterTileLibColor(p)};
  }
`;

export const SpeedBlock = styled.div`
  padding-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const SpeedLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: var(--font-weight-semibold);
  color: #1e293b;
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
`;

export const SpeedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  justify-content: center;
`;

export const SpeedChip = styled.button<{ $active: boolean }>`
  min-width: 100px;
  padding: 0.8rem 0.6rem;
  border-radius: 12px;
  border: 2px solid ${(p) => (p.$active ? infoDark : "#e2e8f0")};
  background: ${(p) => (p.$active ? info : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#64748b")};
  font-weight: var(--font-weight-semibold);
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const PrimaryAction = styled.button`
  width: 100%;
  border: none;
  border-radius: 20px;
  background: ${info};
  border: 3px solid ${infoDark};
  color: #fff;
  padding: 1.2rem 1.6rem;
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
`;

export const GlosaPanel = styled.div`
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  background: #fff;
  padding: 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const GlosaHighlight = styled.div`
  border-radius: 12px;
  border: 2px solid ${info};
  background: rgba(95, 51, 225, 0.08);
  padding: 1.2rem;
  text-align: center;
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  font-weight: var(--font-weight-bold);
  color: ${info};
`;

export const InfoPanel = styled.div`
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  background: #fff;
  padding: 1.4rem;
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  color: #64748b;
`;

export const EmptyHint = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  color: #64748b;
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  flex-direction: column;
  padding: max(1rem, env(safe-area-inset-top))
    max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom))
    max(1rem, env(safe-area-inset-left));
`;

export const ModalTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const ModalTitle = styled.span`
  color: #fff;
  font-weight: var(--font-weight-bold);
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  flex: 1;
`;

export const ModalClose = styled.button`
  background: rgba(255, 255, 255, 0.12);
  border: 2px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 999px;
  width: 44px;
  height: 44px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalStage = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalLetterBox = styled.div`
  width: 100%;
  min-height: 50vh;
  border-radius: 20px;
  border: 4px solid ${info};
  background: rgba(95, 51, 225, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

export const ModalBigLibras = styled.span<{ $hasFont: boolean }>`
  display: block;
  font-family: ${(p) => (p.$hasFont ? '"Libras2020", sans-serif' : "inherit")};
  font-size: clamp(140px, 40vw, 200px);
  color: ${info};
  line-height: 1;
  text-align: center;
`;

export const ModalBigLatin = styled.span`
  display: block;
  margin-top: 1.2rem;
  font-size: ${fontSizes["3xl"]}px;
  line-height: ${lineHeights["3xl"]}px;
  font-weight: var(--font-weight-bold);
  color: #fff;
  text-align: center;
`;

export const ImageModalImg = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 16px;
`;
